import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'
import {
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  ArrowUpRight,
  Ban,
  FileText,
  Loader2,
  Megaphone,
  MousePointerClick,
  Send,
  User as UserIcon,
} from 'lucide-react'
import MultiBarChart from '../components/charts/MultiBarChart'
import { api, apiErrorMessage } from '../lib/api'
import { formatCents } from '../lib/money'
import { currentLocaleTag } from '../lib/formatting'
import { useAuth } from '../contexts/AuthContext'
import {
  MKT_SSO_URL,
  MKT_UNAVAILABLE_PARAM,
  MKT_UNAVAILABLE_VALUE,
  canOpenMktTool,
} from '../lib/marketing'
import { DIAS_ATRIBUCION, type MarketingOverviewResponse } from '@jr2os/shared'

/**
 * La portada de Marketing: el dashboard y las analíticas, en una pantalla.
 *
 * NO hay una pantalla de «Analíticas» aparte aunque Jesús las enumerara como dos
 * epígrafes. Con tres campañas al mes y quinientos contactos, la analítica útil
 * cabe aquí, y mantener dos pantallas significaría mantener dos agregadores y
 * estrenar la primera discrepancia entre dos sitios que dicen contar lo mismo.
 *
 * ☠️ Y la pieza central no es un gráfico: es la TABLA DE QUIÉN HA PULSADO. La
 * pregunta de un lunes no es «cuál fue mi tasa de apertura», es «a quién llamo».
 */
export default function Marketing() {
  const { t } = useTranslation('marketing')
  const auth = useAuth()
  const [params] = useSearchParams()
  const role = auth.status === 'authenticated' ? auth.user.role : null
  const canOpenMkt = canOpenMktTool(role)
  const ssoUnavailable = params.get(MKT_UNAVAILABLE_PARAM) === MKT_UNAVAILABLE_VALUE

  const [dias, setDias] = useState(90)
  const [datos, setDatos] = useState<MarketingOverviewResponse | null>(null)
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const cargar = useCallback(async () => {
    setCargando(true)
    try {
      setDatos(
        await api.get<MarketingOverviewResponse>(`/marketing/stats/overview?days=${dias}`),
      )
      setError(null)
    } catch (err) {
      setError(apiErrorMessage(err, t('overview.errors.load')))
    } finally {
      setCargando(false)
    }
  }, [dias, t])

  useEffect(() => {
    void cargar()
  }, [cargar])

  // La serie diaria solo se pinta si hay más de un día con algo: tres barras
  // aisladas en noventa días no es un gráfico, es ruido con ejes.
  const serie = useMemo(() => {
    if (!datos) return []
    const conAlgo = datos.serie.filter((d) => d.enviados > 0)
    return conAlgo.length >= 2 ? conAlgo : []
  }, [datos])

  const k = datos?.kpis
  const hayDatos = !!k && (k.enviados > 0 || k.bajasNuevas > 0)

  return (
    <div className="space-y-4 animate-slideUp">
      <header className="flex flex-wrap items-end gap-3 justify-between">
        <div>
          <h1 className="text-xl font-serif font-semibold t-primary flex items-center gap-2">
            <Megaphone size={20} className="text-fuchsia-600 dark:text-fuchsia-300" />
            {t('overview.title')}
          </h1>
          <p className="text-xs t-muted">{t('overview.subtitle')}</p>
        </div>
        <label className="text-xs t-muted flex items-center gap-2">
          {t('overview.period')}
          <select
            value={dias}
            onChange={(e) => setDias(Number(e.target.value))}
            className="bg-transparent border border-base rounded px-2 py-1 text-sm t-secondary focus:outline-none focus:border-jr2-500/50"
          >
            <option value={30}>{t('overview.days', { n: 30 })}</option>
            <option value={90}>{t('overview.days', { n: 90 })}</option>
            <option value={365}>{t('overview.days', { n: 365 })}</option>
          </select>
        </label>
      </header>

      {error && (
        <div className="surface p-3 text-sm rounded-lg border text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {cargando && !datos && (
        <div className="surface p-10 text-center t-muted">
          <Loader2 size={18} className="inline-block animate-spin" />
        </div>
      )}

      {datos && !hayDatos && (
        <section className="surface p-8 md:p-12 text-center space-y-4">
          <span className="w-14 h-14 mx-auto grid place-items-center rounded-2xl bg-fuchsia-500/15">
            <Send size={26} className="text-fuchsia-500 dark:text-fuchsia-300" />
          </span>
          <h2 className="text-lg font-bold t-primary">{t('overview.emptyTitle')}</h2>
          <p className="t-secondary max-w-lg mx-auto text-sm">{t('overview.emptyBody')}</p>
          <Link
            to="/marketing/campaigns"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700"
          >
            <Send size={14} /> {t('overview.emptyAction')}
          </Link>
        </section>
      )}

      {datos && hayDatos && k && (
        <>
          {/* ☠️ LA LECTURA, COMO UN RANGO. Publicar «72 % de apertura» sería
              publicar un número que se sabe falso en las dos direcciones: los
              proxies de imágenes lo inflan y quien bloquea imágenes lo hunde. */}
          <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi etiqueta={t('overview.kpis.sent')} valor={String(k.enviados)} />
            <div className="surface p-4">
              <div className="text-[11px] uppercase tracking-wider t-muted">
                {t('overview.kpis.read')}
              </div>
              <div className="font-mono text-2xl t-primary">
                {k.lecturaMinima}
                <span className="t-faint text-lg"> – {k.lecturaMaxima}</span>
              </div>
              <div className="text-[11px] t-faint mt-1">{t('overview.kpis.readHint')}</div>
            </div>
            <Kpi
              etiqueta={t('overview.kpis.failed')}
              valor={String(k.fallidos)}
              tono={k.fallidos > 0 ? 'rose' : undefined}
            />
            <Kpi etiqueta={t('overview.kpis.unsubs')} valor={String(k.bajasNuevas)} />
          </section>

          {datos.remitentesCiegos.length > 0 && (
            <div className="surface p-3 flex items-start gap-2 text-sm border border-amber-500/40 bg-amber-500/10">
              <AlertTriangle size={15} className="text-amber-500 shrink-0 mt-0.5" />
              <p className="text-amber-700 dark:text-amber-300">
                {t('overview.blindSenders', { list: datos.remitentesCiegos.join(', ') })}
              </p>
            </div>
          )}

          {/* ☠️ La pieza que justifica la pantalla. */}
          <section className="surface overflow-hidden">
            <header className="px-4 py-3 border-b border-base">
              <h2 className="t-primary font-semibold text-sm flex items-center gap-2">
                <MousePointerClick size={15} className="text-emerald-600 dark:text-emerald-300" />
                {t('overview.clicksTitle')}
              </h2>
              <p className="text-xs t-muted mt-0.5">{t('overview.clicksHint')}</p>
            </header>
            {datos.clicsRecientes.length === 0 ? (
              <p className="px-4 py-8 text-center t-muted text-sm">{t('overview.clicksEmpty')}</p>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {datos.clicsRecientes.map((c, i) => (
                    <tr key={`${c.email}-${i}`} className="border-t border-base/40 hover:bg-hover">
                      <td className="px-4 py-2">
                        {c.clientId ? (
                          <Link
                            to={`/clients/${c.clientId}`}
                            className="t-primary font-medium hover:underline inline-flex items-center gap-1"
                          >
                            <UserIcon size={12} /> {c.nombre}
                          </Link>
                        ) : (
                          <span className="t-primary">{c.nombre}</span>
                        )}
                        <div className="t-faint text-xs">{c.email}</div>
                      </td>
                      <td className="px-4 py-2 t-secondary">
                        <Link
                          to={`/marketing/campaigns/${c.campanaId}`}
                          className="hover:underline"
                        >
                          {c.campana}
                        </Link>
                        <div className="t-faint text-xs">{c.destino}</div>
                      </td>
                      <td className="px-4 py-2 t-secondary text-right whitespace-nowrap">
                        {new Date(c.cuando).toLocaleString(currentLocaleTag(), {
                          day: '2-digit',
                          month: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          {/* ¿Esto trae trabajo? */}
          {datos.atribucion.oportunidades > 0 && (
            <section className="surface p-4 space-y-2">
              <h2 className="t-primary font-semibold text-sm">{t('overview.workTitle')}</h2>
              <p className="t-secondary text-sm">
                {t('overview.workBody', {
                  n: datos.atribucion.oportunidades,
                  m: datos.atribucion.oportunidadesConInteres,
                  pipeline: formatCents(datos.atribucion.pipelineCents),
                  facturado: formatCents(datos.atribucion.facturadoCents),
                })}
              </p>
              {/* No decir esto sería dejar que un número con euros se lea como
                  una relación de causa y efecto que nadie ha demostrado. */}
              <p className="text-xs t-faint">
                {t('overview.workDisclaimer', { dias: DIAS_ATRIBUCION })}
              </p>
            </section>
          )}

          {serie.length > 0 && (
            <section className="surface p-4">
              <h2 className="t-primary font-semibold text-sm mb-3">{t('overview.seriesTitle')}</h2>
              <MultiBarChart
                data={serie.map((d) => ({
                  label: d.dia.slice(5),
                  values: [d.enviados, d.abiertos, d.clicados],
                }))}
                series={[
                  { name: t('overview.kpis.sent'), color: '#1B4FD8' },
                  { name: t('overview.seriesOpened'), color: '#94a3b8' },
                  { name: t('overview.seriesClicked'), color: '#10b981' },
                ]}
                rotateLabels={serie.length > 12}
              />
            </section>
          )}

          {datos.campanas.length > 0 && (
            <section className="surface overflow-hidden">
              <header className="px-4 py-3 border-b border-base">
                <h2 className="t-primary font-semibold text-sm">{t('overview.campaignsTitle')}</h2>
              </header>
              <table className="w-full text-sm">
                <tbody>
                  {datos.campanas.map((c) => (
                    <tr key={c.id} className="border-t border-base/40 hover:bg-hover">
                      <td className="px-4 py-2">
                        <Link
                          to={`/marketing/campaigns/${c.id}`}
                          className="t-primary font-medium hover:underline"
                        >
                          {c.name}
                        </Link>
                      </td>
                      <td className="px-4 py-2 text-right t-secondary tabular-nums whitespace-nowrap">
                        {t('overview.campaignLine', {
                          enviados: c.enviados,
                          abiertos: c.abiertos,
                          clicados: c.clicados,
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </>
      )}

      {/* Lo que ya funciona del módulo. */}
      <section className="surface p-5">
        <h3 className="font-semibold t-primary mb-3">{t('pieces.title')}</h3>
        <Pieza to="/marketing/campaigns" icono={<Send size={18} />} clave="campaigns" />
        <Pieza to="/marketing/templates" icono={<FileText size={18} />} clave="templates" />
        <Pieza to="/marketing/suppressions" icono={<Ban size={18} />} clave="suppressions" />
      </section>

      {canOpenMkt && (
        <section className="surface p-5 space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold t-primary">{t('current.title')}</h3>
              <p className="text-sm t-secondary mt-1">{t('current.body')}</p>
            </div>
            {/* El botón DESAPARECE si el backend acaba de decir que no puede
                firmar la entrada: ofrecerlo debajo del aviso era invitar a un
                clic que abre otra pestaña para volver a esta misma pantalla. */}
            {!ssoUnavailable && (
              // Navegación real del navegador: el SSO lo firma el backend.
              <a
                href={MKT_SSO_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-200 hover:bg-fuchsia-500/25 transition"
              >
                <Megaphone size={16} />
                {t('current.open')}
                <ArrowUpRight size={14} />
              </a>
            )}
          </div>

          {/* Vuelta del backend cuando la integración SSO no está configurada
              (hoy, la demo): ocupa el sitio del botón, no se suma a él. */}
          {ssoUnavailable && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3">
              <AlertTriangle size={18} className="text-amber-500 shrink-0 mt-0.5" />
              <div className="text-sm">
                <div className="font-semibold t-primary">{t('ssoUnavailable.title')}</div>
                <p className="t-secondary mt-1">{t('ssoUnavailable.body')}</p>
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  )
}

function Kpi({
  etiqueta,
  valor,
  tono,
}: {
  etiqueta: string
  valor: string
  tono?: 'rose'
}) {
  return (
    <div className="surface p-4">
      <div className="text-[11px] uppercase tracking-wider t-muted">{etiqueta}</div>
      <div
        className={`font-mono text-2xl ${
          tono === 'rose' ? 'text-rose-700 dark:text-rose-300' : 't-primary'
        }`}
      >
        {valor}
      </div>
    </div>
  )
}

function Pieza({
  to,
  icono,
  clave,
}: {
  to: string
  icono: React.ReactNode
  clave: string
}) {
  const { t } = useTranslation('marketing')
  return (
    <Link to={to} className="flex items-center gap-3 p-3 -mx-1 rounded-lg hover:bg-hover transition group">
      <span className="w-10 h-10 shrink-0 grid place-items-center rounded-lg bg-fuchsia-500/15 text-fuchsia-600 dark:text-fuchsia-300">
        {icono}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-medium t-primary">{t(`pieces.${clave}.title`)}</span>
        <span className="block text-sm t-secondary">{t(`pieces.${clave}.body`)}</span>
      </span>
      <ArrowRight size={16} className="t-faint group-hover:t-primary shrink-0" />
    </Link>
  )
}
